import axios from 'axios';
import React, { useEffect, useState } from 'react';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import {
	CancellationContext,
	CancellationData,
	withCancellationContext,
} from '../../../context/CancellationProvider';
import AuxDataView from '../../auxData/AuxDataView';
import { StyledContentPane } from '../../common';
import DisabledOverlay from '../../DisabledOverlay';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import LeftSidebarLayout from '../../layouts/LeftSidebarLayout';
import ReinstatementLayout from '../../layouts/ReinstatementLayout';
import ProtectedRoute from '../../ProtectedRoute';
import DocumentsView from '../Cancellations/DocumentsView';
import InvoicesView from '../Cancellations/InvoicesView';
import InvoiceView from '../Cancellations/InvoiceView';
import ReinstatementDetails from '../Cancellations/ReinstatementDetails';
import ReinstatementPricePreview from '../Cancellations/ReinstatementPricePreview';
import ReinstatementSidebar from '../Sidebar/ReinstatementSidebar';

interface Props {
	cancellationData: CancellationData;
}

// Route: '.../reinstatements/:reinstatementId'
const ReinstatementRoutes = (props: Props) => {
	const { cancellationData } = props;
	const {
		selectedCancellation,
		setSelectedCancellation,
		setCancellationUpdateObj,
	} = cancellationData;
	const navigate = useNavigate();
	const params = useParams();
	const { cancellationId, policyId, reinstatementId } = params;
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		const source = axios.CancelToken.source();

		const loadData = async () => {
			try {
				setLoading(true);
				const success = await cancellationData.loadData(
					policyId,
					cancellationId,
					{ cancelToken: source.token },
				);

				if (success) {
					setLoading(false);
				}
			} catch (error) {
				if (axios.isCancel(error)) {
					return;
				}
			}
		};

		if (policyId && cancellationId) {
			loadData();
		}

		return () => {
			source.cancel();
		};
	}, [cancellationId]);

	useEffect(() => {
		return () => {
			setSelectedCancellation(null);
			setCancellationUpdateObj({});
		};
	}, []);

	const renderMain = () => {
		if (!selectedCancellation?.reinstatement) {
			return <LargeCenterSpinner />;
		}

		return (
			<CancellationContext.Provider value={cancellationData}>
				<StyledContentPane>
					<ReinstatementLayout>
						<Routes>
							<Route path='details' element={<ReinstatementDetails />} />
							<Route
								path='documents'
								element={
									<DocumentsView
										documents={selectedCancellation.reinstatement.documents}
										isReinstatement
									/>
								}
							/>
							<Route
								path='invoices/:invoiceId'
								element={
									<ProtectedRoute permission='invoice.fetch'>
										<InvoiceView reinstatementInvoice />
									</ProtectedRoute>
								}
							/>
							<Route
								path='invoices'
								element={
									<ProtectedRoute permission='invoice.fetch'>
										<InvoicesView
											invoiceLocator={
												selectedCancellation.reinstatement.invoiceLocator
											}
										/>
									</ProtectedRoute>
								}
							/>
							<Route path='pricing' element={<ReinstatementPricePreview />} />
							<Route
								path='auxData'
								element={
									<ProtectedRoute permission='auxData.fetch'>
										<AuxDataView
											navigate={navigate}
											entityLocator={reinstatementId}
											entityType={'reinstatement'}
										/>
									</ProtectedRoute>
								}
							/>
							<Route
								path='/'
								element={() => <Navigate to='/details' replace />}
							/>
						</Routes>
					</ReinstatementLayout>
				</StyledContentPane>
			</CancellationContext.Provider>
		);
	};

	const renderSidebar = () => {
		if (loading || !selectedCancellation || !cancellationData) {
			return <DisabledOverlay />;
		}

		return <ReinstatementSidebar />;
	};

	return (
		<CancellationContext.Provider value={cancellationData}>
			<LeftSidebarLayout
				leftSidebarContent={renderSidebar()}
				mainContent={renderMain()}
			/>
		</CancellationContext.Provider>
	);
};

export default withCancellationContext<Props>(ReinstatementRoutes);
